import {
	ContentLoader,
	ContentSidebarLayout,
	FlexGap,
	ItemRowHeader,
	PageHeaderNarrowWrapper,
	StackHorizontal,
	StackVertical,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ItemRowAddon } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/ItemRowAddon';
import React, { useEffect, useRef, useState } from 'react';
import { EndpointError } from '../../_Global/Components/EndpointError';
import MailboxNavigation from '../../Mailboxes/Components/MailboxNavigation';
import { MailItemHeader } from '../Components/Components/MailItemHeader';
import { MailItemContent } from '../Components/MailItemContent';
import { SentMailItemContent } from '../Components/SentItemContent';
import { useMailbox } from '../../Mailboxes/Providers/NewMailboxProvider';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { useMailboxesStore } from '../../Mailboxes/Providers/Stores/MailboxesStore';
import { SelectedMailToolbar } from '../../Mailboxes/Components/Toolbars/SelectedMailToolbar';

function MailItemPage() {
	const { t } = useTranslation();
	const { isTiny, isSmall, isMedium } = useLayout();
	const { updatePageMeta, currentLocale } = useSite();

	const { MarkMailItemAsRead, mailItemData, mailItemStatus, mailItemError, FilterActions, MoveItemsToFolder } =
		useMailbox();

	const mailBoxInfo = useMailboxesStore((state) => state.mailBoxInfo);
	const mailItemOnHold = useMailboxesStore((state) => state.mailItemOnHold);
	const mailItemRequiresTopUp = useMailboxesStore((state) => state.mailItemRequiresTopUp);

	const [mailboxTopBarHeight, setMailboxTopBarHeight] = useState<number>(0);

	const mailToolbarRef = useRef<HTMLDivElement | null>(null);
	const mailBoxNavigationRef = useRef<any>(null);

	useEffect(() => {
		if (mailItemData && !mailItemData.dateRead) {
			MarkMailItemAsRead();
		}

		if (mailItemData && mailItemData.actions?.length > 0) {
			FilterActions(
				[{ mailUuid: mailItemData.mailUuid, actions: mailItemData.actions }],
				[mailItemData.mailUuid],
			);
		}
	}, [mailItemData]);

	useEffect(() => {
		updatePageMeta({
			title:
				mailItemStatus === 'success'
					? `${mailItemData?.trackingId} - ${t('mailItem.title')}`
					: t('mailItem.title'),
		});
	}, [currentLocale, mailItemStatus, mailItemData]);

	useEffect(() => {
		if (mailToolbarRef.current) {
			setMailboxTopBarHeight(mailToolbarRef.current?.clientHeight);
		}
	}, [mailToolbarRef.current]);

	return (
		<ContentSidebarLayout
			navigationComponent={
				!isTiny &&
				!isSmall &&
				!isMedium && (
					<MailboxNavigation
						ref={mailBoxNavigationRef}
						title={mailBoxInfo.folderName}
						createFolderSuccessCallback={(e: any) => MoveItemsToFolder(e)}
					/>
				)
			}
			extraContentClass={`flex-column mail-item-container ${mailItemStatus === 'pending' ? 'disabled' : ''}`}
		>
			{mailItemStatus === 'pending' ? (
				<>
					<PageHeaderNarrowWrapper extraClass='mail-item-page-header'>
						<div className='row'>
							<div className='col-xs-12 col-md-4 col-lg-3'>
								<ContentLoader round childCount={1} childHeight='3.85em' />
							</div>
						</div>
					</PageHeaderNarrowWrapper>

					<ItemRowHeader title='Mailboxes Item Header Loader' extraClass='mail-item-toolbar p-md'>
						<div className='container-narrow'>
							<div className='item-row-toolbar'>
								<StackHorizontal gap={FlexGap.ExtraSmall}>
									<ContentLoader round childCount={1} childHeight='2.5em' childWidth='10em' />
									<ContentLoader round childCount={1} childHeight='2.5em' childWidth='10em' />
									<ContentLoader round childCount={1} childHeight='2.5em' childWidth='10em' />
								</StackHorizontal>
							</div>
						</div>
					</ItemRowHeader>

					<div className='column-adjuster pl-md pr-md'>
						<div className='container-narrow'>
							<div className='row'>
								<div className='col-xs-12 col-lg-4 col-xl-3'>
									<div className='mail-item-meta-grid'>
										<ContentLoader round childCount={1} childHeight='4em' />
										<ContentLoader round childCount={1} childHeight='4em' />
										<ContentLoader round childCount={1} childHeight='4em' />
									</div>
								</div>
								<div className='col-xs-12 col-lg-8 col-xl-9'>
									<div className='mail-item-contents'>
										<StackVertical>
											<StackHorizontal extraClass='mb-sm'>
												<h6 className='pageantry-sub-heading text-left m-0'>
													{t('status.loading')}
												</h6>
											</StackHorizontal>
											<ContentLoader round childCount={1} childHeight='40em' />
										</StackVertical>
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			) : mailItemStatus === 'error' && mailItemError ? (
				<div className='fill-container p-md'>
					<EndpointError {...mailItemError} stacked />
				</div>
			) : (
				<>
					<div className='mail-item-page-header-wrapper sticky' ref={mailToolbarRef}>
						<MailItemHeader />

						{!isTiny && !isSmall && !isMedium && !mailItemOnHold && !mailItemRequiresTopUp && (
							<ItemRowHeader title='Mailboxes Item Header' extraClass='mail-item-toolbar p-md'>
								<ItemRowAddon>
									<div className='container-narrow'>
										<SelectedMailToolbar
											data={mailItemData}
											showCustomFolderRemove={
												!!mailBoxInfo.customFolderId && mailBoxInfo.customFolderId > -1
											}
										/>
									</div>
								</ItemRowAddon>
							</ItemRowHeader>
						)}
					</div>

					<div className='column-adjuster pl-md pr-md'>
						<div className='container-narrow'>
							{mailItemData?.mailLocationId !== 7 && (
								<MailItemContent mailboxTopBarHeight={mailboxTopBarHeight} />
							)}

							{mailItemData?.mailLocationId === 7 && (
								<SentMailItemContent mailboxTopBarHeight={mailboxTopBarHeight} />
							)}
						</div>
					</div>
				</>
			)}
		</ContentSidebarLayout>
	);
}

export default MailItemPage;
